import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { zoneMessages } from 'src/features/zone/utils/messages';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { zoneRuleStayTypeTranslationMap } from 'src/features/membershipType/utils/constants';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { ZoneRuleFormSchemaValues } from 'src/features/membershipType/formSchema';
import { PERIOD_TYPE } from 'src/features/stay/types';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { commonMessages } from 'src/utils/messages';

type ZoneRuleFormProps = {
    form: UseFormReturn<ZoneRuleFormSchemaValues>;
    onSubmit: (values: ZoneRuleFormSchemaValues) => void;
    zoneOptions: { value: string; label?: string }[];
};

export default function ZoneRuleForm({ onSubmit, form, zoneOptions }: ZoneRuleFormProps) {
    const { formatMessage } = useIntl();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...zoneMessages.zone} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(zoneMessages.zone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {zoneOptions?.map((option) => (
                                            <SelectItem key={option?.value} value={option?.value}>
                                                {option?.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<ZoneRuleFormSchemaValues>
                form={form}
                required
                name="allowed_stay_type"
                label={formatMessage(commonMessages.type)}
                options={Object.values(PERIOD_TYPE)}
                optionTranslationMap={zoneRuleStayTypeTranslationMap}
            />

            {form.watch('allowed_stay_type') === PERIOD_TYPE.TRANSIT && (
                <InputFormField<ZoneRuleFormSchemaValues>
                    form={form}
                    name="transit_time_minutes"
                    type="number"
                    required={false}
                    placeholder={`${formatMessage(zoneRulesMessages.transitTime)}...`}
                    label={formatMessage(zoneRulesMessages.transitTimeInMinutes)}
                />
            )}

            {form.watch('allowed_stay_type') === PERIOD_TYPE.STAY && (
                <>
                    <InputFormField<ZoneRuleFormSchemaValues>
                        form={form}
                        name="allowed_spaces_count"
                        type="number"
                        required={false}
                        placeholder={`${formatMessage(zoneRulesMessages.allowedSpacesCount)}...`}
                        label={formatMessage(zoneRulesMessages.allowedSpacesCount)}
                    />

                    <InputFormField<ZoneRuleFormSchemaValues>
                        form={form}
                        name="reserve_spaces_count"
                        type="number"
                        required={false}
                        placeholder={`${formatMessage(zoneRulesMessages.reserveSpaces)}...`}
                        label={formatMessage(zoneRulesMessages.reserveSpaces)}
                    />
                    <InputFormField<ZoneRuleFormSchemaValues>
                        form={form}
                        name="extra_visitors_count"
                        type="number"
                        required={false}
                        placeholder={`${formatMessage(membershipTypeMessages.extraVisitors)}...`}
                        label={formatMessage(membershipTypeMessages.extraVisitors)}
                    />
                    <InputFormField<ZoneRuleFormSchemaValues>
                        form={form}
                        name="allowed_extra_visitors_time_minutes"
                        type="number"
                        required={false}
                        placeholder={`${formatMessage(zoneRulesMessages.allowedExtraVisitorsTime)}...`}
                        label={formatMessage(zoneRulesMessages.allowedExtraVisitorsTimeInMinutes)}
                    />
                </>
            )}
        </Form>
    );
}
